import { useState, useCallback, useMemo } from 'react';

const TOKEN_KEY = 'rtf_token';
const USER_KEY = 'rtf_user';
const BUSINESS_ID_KEY = 'rtf_business_id';
const MANAGE_URL = 'https://manage.realtimefeedback.com';

export type AuthUser = {
  email: string;
  name: string;
  user_type: 'internal' | 'distributor';
};

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

function getStoredItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setStoredItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Authentication remains available to this hook instance in memory.
  }
}

function removeStoredItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage may be blocked by browser privacy or sandbox settings.
  }
}

function getStoredKeys(): string[] {
  try {
    return Object.keys(localStorage);
  } catch {
    return [];
  }
}

export function getAuthToken(): string | null {
  const token = getStoredItem(TOKEN_KEY);
  if (token && isTokenExpired(token)) {
    clearAuth();
    return null;
  }
  return token;
}

export function clearAuth() {
  removeStoredItem(TOKEN_KEY);
  removeStoredItem(USER_KEY);
  removeStoredItem(BUSINESS_ID_KEY);
  // Clear legacy Auth0 state
  for (const key of getStoredKeys()) {
    if (key.startsWith('@@auth0')) {
      removeStoredItem(key);
    }
  }
}

export function redirectToLogin() {
  clearAuth();
  window.location.href = MANAGE_URL;
}

export function useAuth() {
  const [token, setTokenState] = useState<string | null>(() => getAuthToken());
  const [user, setUserState] = useState<AuthUser | null>(() => {
    const stored = getStoredItem(USER_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as AuthUser;
    } catch {
      return null;
    }
  });

  const isAuthenticated = useMemo(() => {
    if (!token) return false;
    return !isTokenExpired(token);
  }, [token]);

  const login = useCallback(
    (jwt: string, authUser: AuthUser, businessId?: number | null) => {
      setStoredItem(TOKEN_KEY, jwt);
      setStoredItem(USER_KEY, JSON.stringify(authUser));
      if (businessId) {
        setStoredItem(BUSINESS_ID_KEY, String(businessId));
      }
      setTokenState(jwt);
      setUserState(authUser);
    },
    [],
  );

  const logout = useCallback(() => {
    redirectToLogin();
  }, []);

  return { token, user, isAuthenticated, login, logout };
}
